import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Hotspot from 'threejs_scene/Hotspot';
// import AnimatedGLB from 'threejs_scene/AnimatedGLB';
import FireEffect from 'threejs_scene/FireEffect';
import GreenScreenSystem from 'threejs_scene/GreenScreenSystem';
import InteractiveGLB from 'threejs_scene/InteractiveGLB';
import {
	ICON_NAMES,
	IN_SCENE_VIDEO_PAUSE_ICON,
	IN_SCENE_VIDEO_PLAY_ICON,
} from '@wsc/utils/hotspotConstants';
import { formURL } from '@wsc/utils/apiUtils';
import { extractHotspotInfo } from '@wsc/utils/sceneUtils';
import { setSoundHotspot } from '@wsc/redux_stores/soundHotspotReducer';
import { setModalProps } from '@wsc/redux_stores/modalsReducer';
import {
	popFromMediaStack,
	pushToMediaStack,
} from '@wsc/redux_stores/mediaControllerReducer/actions';
import { getFilename } from '@wsc/utils/fileUtils';
import { useAnalytics } from '@wsc/hooks';
import { createRoomObjectDataSelector } from '@wsc/redux_stores/roomObjectsReducer';
import { createIsHotspotActiveAdaSelector } from '@wsc/redux_stores/accessibilityReducer';

const storeDataSelector = createSelector(
	[
		(state) => state?.defaultIcons || {},
		(state) => state?.storeData?.styling?.icons || {},
		(state) => state?.storeData?.styling?.store_icon_files || {},
		(state) => state?.storeData?.sfx_assets?.product || {},
		(state) => state?.storeData?.sfx_assets?.content || {},
	],
	(
		defaultIcons,
		stylingIcons,
		storeIconFiles,
		storeProductSoundFile,
		storeContentSoundFile,
	) => ({
		defaultIcons,
		stylingIcons,
		storeIconFiles,
		storeProductSoundFile,
		storeContentSoundFile,
	}),
);

const createHotspotModalActiveSelector = (hotspotId) =>
	createSelector([(state) => state.modalData], (modalData) =>
		Object.keys(modalData).some((key) => {
			return (
				modalData[key].hotspotId === hotspotId && modalData[key].visible
			);
		}),
	);

const HotspotMarker = ({ hotspotId, ...props }) => {
	const dataSelector = useMemo(
		() => createRoomObjectDataSelector(hotspotId),
		[hotspotId],
	);
	const adaActiveSelector = useMemo(
		() => createIsHotspotActiveAdaSelector(hotspotId),
		[hotspotId],
	);

	const hotspotData = useSelector(dataSelector);

	const isActiveAda = useSelector(adaActiveSelector, shallowEqual);

	const {
		defaultIcons,
		stylingIcons,
		storeIconFiles,
		storeProductSoundFile,
		storeContentSoundFile,
	} = useSelector(storeDataSelector);

	const { hotspot_type: hotspotType, selector } = hotspotData?.props || {};

	const isActiveModalSelector = useMemo(
		() => createHotspotModalActiveSelector(hotspotId),
		[hotspotId],
	);

	const isActive = useSelector(isActiveModalSelector);

	const { collect } = useAnalytics();

	const dispatch = useDispatch();

	const getTooltipConfig = () => {
		if (hotspotData.props?.tooltip?.image?.url) {
			const config = JSON.parse(
				JSON.stringify(hotspotData.props?.tooltip),
			);
			config.image.url = formURL(config.image.url);
			return config;
		}
		return hotspotData.props?.tooltip;
	};

	const getHotspotImage = (specificIcon, hotspotState = 'default') => {
		if (
			hotspotData.hasOwnProperty('scene_object_type') &&
			hotspotData.hasOwnProperty('scene_object_props')
		) {
			return formURL(hotspotData.scene_object_props.url);
		}

		if (isProductImage) {
			return formURL(hotspotData?.props?.image?.file?.url);
		}

		if (isProductImage || isImage) {
			return formURL(hotspotData?.props?.url);
		}

		const iconNameKey = ICON_NAMES?.[hotspotType]?.[hotspotState];
		const hotspotIconSearchKey = stylingIcons[iconNameKey]?.name;
		let url = '';
		if (hotspotIconSearchKey in defaultIcons) {
			url = formURL(defaultIcons[hotspotIconSearchKey]);
		}
		if (hotspotIconSearchKey in storeIconFiles) {
			url = formURL(storeIconFiles[hotspotIconSearchKey]?.url);
		}
		if (specificIcon) {
			url = formURL(specificIcon);
		}

		return url;
	};

	// const isAnimatedGlb =
	// 	hotspotType === 'custom' && selector === 'animated_glb';

	const isEmbeddedVideo = hotspotType === 'embedded_video';

	const isImage = hotspotType === 'embedded_image';

	const isProductImage = hotspotType === 'product_image';

	const isGreenScreenSystem =
		hotspotType === 'custom' && selector === 'green_screen_system';
	const isFireEffect = hotspotType === 'custom' && selector === 'fire_effect';
	const isWaterEffect =
		hotspotType === 'custom' && selector === 'water_effect';

	const isInteractiveGlb =
		hotspotType === 'custom' && selector === 'interactive_glb';

	const inSceneVideoPlayOnClick = () => {
		collect({
			event_name: 'click_content',
			action: 'In-scene video played',
			content_name: getFilename(formURL(hotspotData?.props?.url)),
			content_URL: formURL(hotspotData?.props?.url),
			hotspot_id: hotspotData?._id?.$oid,
			hotspot_name: hotspotData?.name,
			hotspot_type: hotspotData?.props?.hotspot_type,
		});
	};

	const inSceneVideoPauseOnClick = () => {
		collect({
			event_name: 'click_content',
			action: 'In-scene video paused',
			content_name: getFilename(formURL(hotspotData?.props?.url)),
			content_URL: formURL(hotspotData?.props?.url),
			hotspot_id: hotspotData?._id?.$oid,
			hotspot_name: hotspotData?.name,
			hotspot_type: hotspotData?.props?.hotspot_type,
		});
	};

	const interactiveGlbOnClick = () => {
		// UA to deprecate
		collect({
			event_name: 'click_custom',
			action: '3D product interacted',
			content_name: getFilename(hotspotData?.props?.data?.glbObjectUrl),
			content_URL: hotspotData?.props?.data?.glbObjectUrl,
			hotspot_id: hotspotData?._id?.$oid,
			hotspot_name: hotspotData?.props?.name,
			hotspot_type: hotspotData?.props?.hotspot_type,
		});
	};

	const labelProps = {};

	if (hotspotData.type === 'Label') {
		labelProps.containerStyling = hotspotData?.props?.container;
		labelProps.labelStyling = hotspotData?.props?.label;

		if (hotspotData?.props?.hotspot_type === 'tooltip') {
			labelProps.arrowConfig = {
				arrow_color: hotspotData?.props?.arrow_color,
				arrow_direction: hotspotData?.props?.arrow_direction,
			};
			labelProps.visible = hotspotData?.props?.show_on_load || false;

			if (hotspotData?.props?.image) {
				labelProps.imageURL = formURL(hotspotData.props.image);
			}
		}
	}

	const {
		isEnabled: creatorToolsEnabled,
		hideProductHotspots,
		hideContentHotspots,
	} = useSelector((state) => state?.creatorTools);

	const isProductHotspot = hotspotType === 'product';

	const hideForCreatorTools =
		(isProductHotspot && hideProductHotspots) ||
		(!isProductHotspot && hideContentHotspots);

	let hotspotConfig = {};

	if (hotspotType === '3d_model') {
		hotspotConfig = {
			...hotspotConfig,
			type: '3d_model',
			visualObjectConf: {
				url: hotspotData?.props?.url && formURL(hotspotData.props.url),
				is_animation_on: hotspotData?.props?.is_animation_on,
			},
		};
	}

	const getHotspotSound = () => {
		if (hotspotData.props?.sfx_asset) {
			return formURL(hotspotData.props.sfx_asset);
		} else if (isProductHotspot) {
			return formURL(storeProductSoundFile);
		} else if (storeContentSoundFile) {
			return formURL(storeContentSoundFile);
		}
		return null;
	};

	const getHotspotType = () => {
		const isProductImage = hotspotType === 'product_image';
		const isImage = hotspotType === 'embedded_image';
		const isSceneTypeObjectImage =
			hotspotData?.scene_object_type === 'image';

		if (isProductImage || isImage || isSceneTypeObjectImage) {
			return 'image_hotspot';
		}
		if (hotspotType === 'custom' && selector === 'text_object') {
			return 'in_scene_text';
		}
		if (hotspotData?.scene_object_type) {
			return hotspotData?.scene_object_type;
		}
		return hotspotData?.type;
	};

	const onLinkMarkerClicked = (data) => {
		const linkUrl = formURL(data.url);
		window.open(linkUrl, '_blank');
		const hotspotInfo = extractHotspotInfo(data);
		collect({
			event_name: 'click_content',
			action: 'Link hotspot clicked',
			content_url: linkUrl,
			...hotspotInfo,
		});
	};

	const sendGaTrackingData = (data) => {
		const hotspotInfo = extractHotspotInfo(data);
		if (data?.hotspot_type === 'product') {
			collect({
				event_name: 'click_product',
				action: 'Product hotspot clicked',
				item_id: data.product_sku,
				item_name: data.name,
				currency: data.currency,
				value: data.price,
				...hotspotInfo,
			});
			return;
		}
		collect({ ...data, ...hotspotInfo });
	};

	const handleSceneMeshVisibilityClick = ({ states = {} }) => {
		Object.keys(states).forEach((meshName) => {
			window.dispatchEvent(
				new CustomEvent('scene_toggle_mesh_visibility', {
					detail: {
						meshName,
						visible: states[meshName],
					},
				}),
			);
		});
	};

	const onClicked = (auxData) => {
		const toResolve = auxData?.hotspot_type
			? auxData.hotspot_type
			: hotspotType;

		const propsToPass = auxData?.hotspot_type
			? { ...auxData }
			: { ...hotspotData?.props };

		// NOTE: `hotspotId` is explicitly added to the data returned from the click event.
		// If you see any weird values being passed, check the flow for the click event.
		propsToPass.hotspotId = hotspotData?._id?.$oid || '';

		// Replaces onHotpotMarkerClicked from Room.jsx
		switch (toResolve) {
			case 'link':
				onLinkMarkerClicked(propsToPass);
				break;
			case 'sound':
				dispatch(setSoundHotspot(hotspotData?._id?.$oid));
				break;
			case 'product_carousel':
				dispatch(
					setModalProps({
						key: 'product',
						props: {
							...propsToPass,
							visible: true,
							carousel: true,
							name: hotspotData?.name,
						},
					}),
				);
				break;
			case 'custom':
				const customSelector = hotspotData?.props?.selector;
				switch (customSelector) {
					case 'glb_event_hotspot':
						document.dispatchEvent(
							new CustomEvent(customSelector, {
								detail: propsToPass,
							}),
						);
						break;
					case 'event_hotspot_trigger':
						document.dispatchEvent(
							new CustomEvent(propsToPass?.data?.actionType, {
								detail: propsToPass?.data?.actionData,
							}),
						);
						break;
					case 'interactive_glb':
						if (!propsToPass?.data?.modal_selector) break;
						interactiveGlbOnClick();
						dispatch(
							setModalProps({
								key: propsToPass?.data.modal_selector,
								props: {
									...propsToPass.data,
									visible: true,
								},
							}),
						);
						break;
					case 'scene_khr_variant_selector':
						const variantName = propsToPass?.data?.variantName;
						if (!variantName) break;
						window.dispatchEvent(
							new CustomEvent('set_scene_model_KHR_variant', {
								detail: { variant: variantName },
							}),
						);
						break;
					case 'set_scene_meshes_visibility':
						handleSceneMeshVisibilityClick(propsToPass.data);
						break;
					default:
						dispatch(
							setModalProps({
								key: propsToPass.selector,
								props: {
									...propsToPass,
									visible: true,
									hotspotId,
								},
							}),
						);
				}
				if (propsToPass?.data?.gaConfig) {
					const gaConfig = propsToPass?.data?.gaConfig || {};
					sendGaTrackingData({ ...gaConfig });
				}
				break;
			case '3d_model':
				const modalSelector = hotspotData?.props?.data?.selector;
				switch (modalSelector) {
					case modalSelector:
						dispatch(
							setModalProps({
								key: modalSelector,
								props: {
									...propsToPass.data,
									visible: true,
								},
							}),
						);
						break;
					default:
						break;
				}
			default:
				dispatch(
					setModalProps({
						key: propsToPass.hotspot_type,
						props: {
							...propsToPass,
							visible: true,
							name: hotspotData?.name,
						},
					}),
				);
		}
	};
	if (isFireEffect) {
		return (
			<FireEffect
				{...props}
				roomId={hotspotData.scene.$oid}
				position={hotspotData.props.data.position}
				scale={hotspotData.props.data.scale}
				rotation={hotspotData.props.data.rotation}
				magnitude={hotspotData.props.data.magnitude}
				lacunarity={hotspotData.props.data.lacunarity}
				gain={hotspotData.props.data.gain}
				speed={hotspotData.props.data.speed}
			/>
		);
	}
	if (isWaterEffect) {
		return null;
	}
	if (isGreenScreenSystem) {
		return (
			<GreenScreenSystem
				{...props}
				id={hotspotData._id.$oid}
				srcs={hotspotData.props.data.srcs}
				similarity={hotspotData.props.data.similarity}
				smoothness={hotspotData.props.data.smoothness}
				spill={hotspotData.props.data.spill}
				autoPlay={hotspotData.props.data?.autoPlay}
				keyColor={hotspotData.props.data.keyColor}
				roomId={hotspotData.scene.$oid}
				loop={hotspotData.props.data?.loop}
			/>
		);
	}
	// if (isEmbeddedVideo) {
	// 	if (
	// 		hotspotData?.props?.hasOwnProperty('show_icon') &&
	// 		!hotspotData?.props?.show_icon
	// 	)
	// 		return <></>;
	// 	return (
	// 		<InSceneVidComponent
	// 			{...props}
	// 			src={formURL(hotspotData?.props?.url)}
	// 			transform={hotspotData.transform}
	// 			userData={{
	// 				props: hotspotData?.props,
	// 				type: hotspotData.type,
	// 				recordId: hotspotData._id.$oid,
	// 				...hotspotData,
	// 			}}
	// 			keyColor={hotspotData?.props?.chroma_key}
	// 			inSceneVideoPlayOnClick={inSceneVideoPlayOnClick}
	// 			inSceneVideoPauseOnClick={inSceneVideoPauseOnClick}
	// 			addToMediaStack={addToMediaStackWrapper}
	// 			popFromMediaStack={popFromMediaStackWrapper}
	// 			playIconUrl={IN_SCENE_VIDEO_PLAY_ICON}
	// 			pauseIconUrl={IN_SCENE_VIDEO_PAUSE_ICON}
	// 		/>
	// 	);
	// }

	// if (isAnimatedGlb) {
	// 	return (
	// 		<AnimatedGLB
	// 			scene={hotspotData.scene.$oid}
	// 			collect={collect}
	// 			{...props}
	// 		/>
	// 	);
	// }

	if (isInteractiveGlb) {
		// NOTE: Saved for reference; Do not delete if you see this. React.lazy, dynamic import, Suspense.
		// const InteractiveGLB = React.lazy(() =>
		// 	import('threejs_scene/InteractiveGLB'),
		// );
		// return (
		// 	<Suspense fallback={<div>Loading...</div>}>
		// 		<InteractiveGLB
		// 			{...props}
		// 			hotspotData={hotspotData}
		// 			onClick={onClicked}
		// 		/>
		// 	</Suspense>
		// );

		return (
			<InteractiveGLB
				{...props}
				hotspotData={hotspotData}
				onClick={onClicked}
				reportEvent={(event) => {
					collect(event);
				}}
				formURL={formURL}
			/>
		);
	}

	const visualObjectConf = {
		animationType:
			hotspotData?.props?.animation_type ||
			hotspotData?.userData?.props?.animation_type,
	};

	if (isEmbeddedVideo) {
		visualObjectConf.videoSource = formURL(hotspotData?.props?.url);
		visualObjectConf.keyColor = hotspotData?.props?.chroma_key;
		visualObjectConf.playIconUrl = IN_SCENE_VIDEO_PLAY_ICON;
		visualObjectConf.pauseIconUrl = IN_SCENE_VIDEO_PAUSE_ICON;
		visualObjectConf.onPaused = (isUserAction) => {
			if (hotspotData?.props?.muted === false) {
				dispatch(popFromMediaStack());
			}
			isUserAction && inSceneVideoPauseOnClick();
		};
		visualObjectConf.onPlayed = (dom) => {
			if (hotspotData?.props?.muted === false) {
				dispatch(pushToMediaStack(dom));
			}
			inSceneVideoPlayOnClick();
		};
		visualObjectConf.onEnded = () => {
			if (hotspotData?.props?.muted === false) {
				dispatch(popFromMediaStack());
			}
		};
	}

	const getVisibility = () => {
		if (
			hotspotType === '3d_model' &&
			hotspotData?.props?.hasOwnProperty('show_icon')
		) {
			return hotspotData?.props?.show_icon;
		}

		if (
			hotspotType === 'embedded_image' &&
			hotspotData?.props?.hasOwnProperty('show_icon')
		) {
			return hotspotData?.props?.show_icon;
		}
		return true;
	};

	if (getHotspotType() === '3d_model') {
		visualObjectConf.url = getHotspotImage(
			hotspotData?.props?.icon,
			'default',
		);
	}

	return (
		<Hotspot
			key={`hotspot-${hotspotId}`}
			{...props} // Required to pass the props from threejs-scene.Scene
			type={getHotspotType()}
			collider_transform={hotspotData.collider_transform}
			transform={hotspotData.transform}
			visualObjectConf={visualObjectConf}
			focusOnClick={hotspotData?.focus_on_click || false}
			iconConfig={{
				showIcon:
					creatorToolsEnabled && hideForCreatorTools
						? false
						: hotspotData?.props?.show_icon,
			}}
			userData={{
				...hotspotData,
				props: {
					...hotspotData?.props,
					hotspotId: hotspotData?._id?.$oid || '',
				},
			}}
			visible={getVisibility()}
			imageURL={getHotspotImage(hotspotData?.props?.icon, 'default')}
			imageHoverURL={getHotspotImage(
				hotspotData?.props?.hover_icon,
				'hover',
			)}
			soundURL={getHotspotSound()}
			isActive={isActive || isActiveAda}
			{...labelProps}
			{...hotspotConfig}
			onClick={onClicked}
			tooltip={
				hotspotData.props?.tooltip?.enabled
					? getTooltipConfig()
					: { enabled: false }
			}
		/>
	);
};

HotspotMarker.propTypes = {
	hotspotId: PropTypes.object.isRequired,
};

export default HotspotMarker;
